import React, { useState, useEffect, useContext, useCallback } from "react";
import "./RunProjectView.scss";
import RunStatusHeader from "./Header/RunStatusHeader";
import ProjectInfo from "./ProjectInfo/ProjectInfo";
import ActionUpdates from "./ActionUpdates/ActionUpdates";
import {
    ProjectListItem,
    ProjectRunStatus,
    ProjectUpdatedMessage,
    SubscriptionMessageStatusType,
    SubscriptionMessageType
} from "./types";

import { Tag } from "@blueprintjs/core";
import { Tooltip2 } from "@blueprintjs/popover2";
import { useAutomationSubscription } from "hooks/useProjectUpdate";
import { SubscriptionClientContext } from "utils/context";
import { CopyToaster } from "utils/toasters";

type ConnectionState = "CONNECTING" | "OPEN" | "CLOSING" | "CLOSED";
const connectionStateArray: ConnectionState[] = ["CONNECTING", "OPEN", "CLOSING", "CLOSED"];

type Props = {
    taskId: string;
};
export default function RunProjectView({ taskId }: Props) {
    const [autoprojId, setAutoprojId] = useState<string>(taskId);
    const [subscriptionConnected, setSubscriptionConnected] = useState<boolean | undefined>(undefined);
    const [subscriptionConnectError, setsubscriptionConnectError] = useState<boolean>(false);
    const [projectAutomate, setProjectAutomate] = useState<boolean | undefined>(undefined);
    const [projectAutomateError, setProjectAutomateError] = useState<boolean>(false);
    const [projectInprogress, setProjectInprogress] = useState<boolean | undefined>(undefined);
    const [updatedMessages, setUpdatedMessage] = useState<Array<ProjectListItem>>([]);
    const [projecInfoMessage, setProjectInfoMessage] = useState<string>("Running...");
    const [projectStatus, setProjectStatus] = useState<ProjectRunStatus>("");
    const { data, loading, error } = useAutomationSubscription(taskId);

    const client = useContext(SubscriptionClientContext);
    const [state, setState] = useState<ConnectionState>("CONNECTING");

    useEffect(() => {
        if (client) {
            setState(connectionStateArray[client.status]);

            const callbacks = [
                client.on("connecting", () => setState("CONNECTING")),
                client.on("connected", () => setState("OPEN")),
                client.on("reconnecting", () => setState("CONNECTING")),
                client.on("reconnected", () => setState("OPEN")),
                client.on("disconnected", () => setState("CLOSED")),
                client.on("error", () => setState("CLOSED"))
            ];

            return () => {
                for (const c of callbacks) {
                    c();
                }
            };
        }
    }, [client]);

    useEffect(() => {
        console.log("ws state", state);
        console.log("loading", loading, error);
        setSubscriptionConnected(!loading);
        setsubscriptionConnectError(!!error);
    }, [state, loading, error]);

    const updateActionCompleted = useCallback(
        (actionCompletedData: ProjectUpdatedMessage) => {
            console.log("actionCompletedData", actionCompletedData);
            const { actionCompletedOutput, actionId, skipped, canceled } = actionCompletedData;

            const action = updatedMessages.find((m) => m.actionId === actionId);

            const currStatus = skipped ? "Skipped" : canceled ? "Canceled" : "Completed";

            if (!action) {
                setProjectStatus("running");

                const pli: ProjectListItem = {
                    actionId: actionId,
                    status: currStatus,
                    results: actionCompletedOutput
                };
                setUpdatedMessage([...updatedMessages, pli]);
                return;
            }

            action.status = currStatus;
            action.results = actionCompletedOutput;

            //setUpdatedMessage([...updatedMessages]);
        },
        [updatedMessages]
    );

    const updateProjectMessage = useCallback(
        (newMessage: ProjectUpdatedMessage) => {
            console.log("newMessage", newMessage);
            if (newMessage.runtimeStatus === "Failed") {
                setProjectStatus("error");
                return;
            } else if (newMessage.runtimeStatus === "Running") {
                if (!newMessage.actionId) {
                    setProjectStatus("running");
                    return;
                }

                if (!newMessage.actionId) return;

                const actionId = newMessage.actionId.split("-")[0];

                const action = updatedMessages.find((m) => m.actionId === actionId);

                if (!action) {
                    const pli: ProjectListItem = {
                        actionId: actionId,
                        status: newMessage.runtimeStatus,
                        results: []
                    };
                    setUpdatedMessage([...updatedMessages, pli]);
                }
            }
        },
        [updatedMessages]
    );

    const updateProjectStatus = useCallback(
        (
            projectState: SubscriptionMessageStatusType,
            inProgress: boolean,
            error: string | undefined,
            message: string | undefined,
            projectUpdatedMessage?: ProjectUpdatedMessage
        ) => {
            console.log("updateProjectStatus", projectState, projectUpdatedMessage);
            if (projectState === SubscriptionMessageStatusType.COMPILE) {
                setSubscriptionConnected(inProgress);

                if (error) setsubscriptionConnectError(true);
            } else if (projectState === SubscriptionMessageStatusType.RUNNING) {
                setProjectAutomate(inProgress);
                setProjectInfoMessage(message || "");
                setAutoprojId(`Automation Request ID: ${taskId}`);
                if (error) setProjectAutomateError(true);
            } else if (projectState === SubscriptionMessageStatusType.DONE) {
                setProjectInprogress(false);

                if (!projectUpdatedMessage) {
                    console.log("no project status was returned by subscription");
                    setProjectStatus("error");
                    setProjectInfoMessage("Project status was not found");
                    return;
                }

                if (projectUpdatedMessage.error) {
                    console.log("project output error");

                    setProjectStatus("error");
                    setProjectInfoMessage(projectUpdatedMessage.error);
                    return;
                }
                setProjectStatus("done");
                setProjectInfoMessage(projectUpdatedMessage.projectOutput || "Project did not define any outputs.");
            }
        },
        [taskId]
    );

    useEffect(() => {
        const autoReqUpdate = data?.orchProjectUpdate;
        if (autoReqUpdate) {
            const { eventType, error } = autoReqUpdate;
            let message = "";
            if (eventType === SubscriptionMessageType.PROJECT_STEP_STATUS) {
                message = `${autoReqUpdate.instanceId} - ${autoReqUpdate.runtimeStatus} ${
                    autoReqUpdate.reason !== "" ? " - " + autoReqUpdate.reason : ""
                }`;
                if (autoReqUpdate.actionId !== "processOutputs") {
                    const projectUpdatedMessage = {
                        runtimeStatus: autoReqUpdate.runtimeStatus,
                        actionId: autoReqUpdate.actionId,
                        reason: autoReqUpdate.reason
                    };
                    updateProjectMessage(projectUpdatedMessage);
                    updateProjectStatus(
                        SubscriptionMessageStatusType.RUNNING,
                        true,
                        error,
                        message,
                        projectUpdatedMessage
                    );
                }
            } else if (eventType === SubscriptionMessageType.ACTION_COMPLETED) {
                const output = autoReqUpdate.skipped
                    ? [autoReqUpdate.skipped]
                    : autoReqUpdate.canceled
                    ? [autoReqUpdate.canceled]
                    : autoReqUpdate.actionCompletedOutput;
                const projectUpdatedMessage = {
                    actionId: autoReqUpdate.actionId,
                    actionCompletedOutput: output,
                    skipped: autoReqUpdate.skipped,
                    canceled: autoReqUpdate.canceled,
                    runtimeStatus: autoReqUpdate.runtimeStatus
                };
                updateActionCompleted(projectUpdatedMessage);
            } else if (eventType === SubscriptionMessageType.PROJECT_STEP_DONE) {
                const projectUpdatedMessage = {
                    projectOutput: autoReqUpdate.projectOutput,
                    error: autoReqUpdate.error,
                    runtimeStatus: autoReqUpdate.runtimeStatus
                };
                updateProjectMessage(projectUpdatedMessage);
                updateProjectStatus(
                    SubscriptionMessageStatusType.RUNNING,
                    false,
                    error,
                    message,
                    projectUpdatedMessage
                );
                updateProjectStatus(SubscriptionMessageStatusType.DONE, false, error, message, projectUpdatedMessage);
            }
        }
    }, [data, updateActionCompleted, updateProjectMessage, updateProjectStatus]);

    const handleTagClick = useCallback(() => {
        navigator.clipboard.writeText(autoprojId);
        CopyToaster.show({
            message: `Copied to Clipboard`,
            intent: "success"
        });
    }, [autoprojId]);

    return (
        <div className="main">
            <RunStatusHeader
                connected={subscriptionConnected}
                connectionError={subscriptionConnectError}
                automate={projectAutomate}
                automateError={projectAutomateError}
                inProgress={projectInprogress}
            />
            <div className="request-id">
                <Tag large>Request Id</Tag>
                <Tooltip2 content="Copy" placement="bottom-end" minimal>
                    <Tag large intent="primary" interactive onClick={handleTagClick}>
                        {autoprojId}
                    </Tag>
                </Tooltip2>
            </div>
            <div className="run-info">
                <ProjectInfo projectStatus={projectStatus} message={projecInfoMessage} />

                {!!updatedMessages.length ? <ActionUpdates messages={updatedMessages} /> : null}
            </div>
        </div>
    );
}
